import { Observer, ReplaySubject, Subject, distinctUntilChanged, distinctUntilKeyChanged, filter } from "rxjs";

const DEBUG = false;
export class CacheListener<T> extends ReplaySubject<T> {
    private currentValue: T;
    private promise: Promise<T>;
    private _sync: (...args: any[]) => Promise<T>;
    private _clearSubject: Subject<void> = new Subject();
    private bindings: CacheListener<any>[] = [];
    public name: string;

    constructor(name: string, value: T, sync: (...args: any[]) => Promise<T>) {
        super(1);
        this.name = name;
        this.currentValue = value; // Initialize currentValue
        this._sync = sync;

        if (value !== null) this.next(value);
    }

    async get() {
        if (this.promise) return await this.promise;
        if (this.currentValue == null) return await this.sync();

        return this.currentValue;
    }

    onValue (callback: (T) => any) {
        this.get();

        return this.subscribe(callback);
    }

    async sync(...args: any[]) {
        DEBUG && console.log(`Syncing ${this.name}...`, this.promise);
        if (this.promise) return this.promise;

        // Create the lock immediately, wrapping the whole flow.
        this.promise = (async () => {
            try {
            const values = await Promise.all(this.bindings.map(b => b.get()));
            const missing = values
                .map((v, i) => (v == null ? i : -1))
                .filter(i => i !== -1)
                .map(i => this.bindings[i].name);

            if (missing.length) {
                DEBUG && console.log(
                `Could not sync ${this.name} because dependencies ${missing.join(', ')} are missing.`
                );
                this.clear();
                return null;
            }

            DEBUG && console.log(`Executing sync for ${this.name}...`);

            const result = await this._sync(...args);

            if (result == null) {
                DEBUG && console.log(`Could not sync ${this.name} because it returned null.`);
                this.clear();
                return null;
            }

            this.currentValue = result;
            this.next(result);
            return result;
            } finally {
            // release the lock no matter what
            this.promise = null;
            }
        })();

        return this.promise;
    }


    async clear() {
        if (this.currentValue === null) return;
        this.currentValue = null;
        this.promise = null;

        this._clearSubject.next();
    }

    onClear (observer: Partial<Observer<void>> | (() => void)) {
        return this._clearSubject.pipe(filter(() => this.currentValue == null)).subscribe(observer);
    }

    bind (listener: CacheListener<any>, { required }: { required: boolean } = { required: true }) {
        if (required) this.bindings.push(listener);
        return listener.onClear(this.clear.bind(this));
    }

    syncWhenPropertyChanges<X> (listener: CacheListener<X>, property: keyof X, ) {
        return listener.pipe(distinctUntilKeyChanged(property)).subscribe(() => this.sync());
    }

    syncWhenPropertiesChanges<X> (listener: CacheListener<X>, properties: Array<keyof X>) {
        return listener.pipe(distinctUntilChanged((prev, cur) =>
            properties.every(prop => prev[prop] === cur[prop])
        )).subscribe(() => this.sync());
    }

    syncWhenChanges (listener: CacheListener<any>) {
        return listener.pipe(distinctUntilChanged((prev, cur) =>
            prev === cur
        )).subscribe(() => this.sync());
    }

    syncWhen<X> (listener: CacheListener<X>, callback: (prev: X, cur: X) => boolean) {
        return listener.pipe(distinctUntilChanged(callback)).subscribe(() => this.sync());
    }
}
